import { Box, Button, Grid, Stack, Tab, TablePagination, Tabs, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails, IPostContactSupplier } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierForm } from '../../../presentation-components/organisation-components/supplier/supplier-form';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { UploadOutlined, } from '@mui/icons-material';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { ImportMappingSupplierForm } from '../../../presentation-components/organisation-components/supplier/import-mapping-supplier-form';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import FileUploadSettingService from '../../../../shared/services/file-upload-setting/file-upload-setting.service';
import { IFileUploadSetting } from '../../../../shared/services/file-upload-setting/file-upload-setting.model';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { getTemplateFileName } from '../../../../shared/utils/import-utils';
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service';
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import { EmissionsInputCard } from '../../../presentation-components/emissons-components/overview/emissions-display/emissions-input-card';
import { NotesPanel } from '../../../presentation-components/notes-components/notes-panel';
import { useNotePeriodOptions } from '../../../presentation-components/notes-components/use-note-period-options';

export const OrganisationSupplierEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  //Services
  const fileUploadSettingService = new FileUploadSettingService()
  const contactSupplierService = ContactSupplierService.getInstance();
  const capitalGoodService = new CapitalGoodService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext?.userDetails
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  const notePeriodOptions = useNotePeriodOptions()
  // Supplier notes are written by admins and analysts (a data owner has no supplier scope).
  const canWriteNotes = !!userDetails?.roles?.some((role) => role === 'admin' || role === 'analyst')
  //State
  const [fileUploadSettings, setFileUploadSettings] = useState<IFileUploadSetting[]>()
  const [supplier, setSupplier] = useState<IContactSupplierDetails>()
  const [editSupplier, setEditSupplier] = useState<IPostContactSupplier>()
  const [editField, setEditField] = useState<string>('')
  const [value, setValue] = React.useState(0)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasOpenReport, setHasOpenReport] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  //Table State
  const [inputPageEmission, setInputPageEmission] = useState(0);
  const [change, setChange] = useState<number>(0)
  //Products Tab State
  const goodsPerPage = 12
  const [supplierGoods, setSupplierGoods] = useState<ICapitalGood[]>()
  const [goodsCount, setGoodsCount] = useState<number>(0)
  const [goodsPage, setGoodsPage] = useState<number>(0)
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Use Effects
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        const tabIndex = parseInt(token)
        setValue(tabIndex)
        if (tabIndex === 2) {
          setIsLoading(false)
        }
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
  }, [])

  useEffect(() => {
    if (id) {
      if (value === 0) {
        getSupplier(id)
      } else if (value === 1) {
        getFileUploadSettings(id)
      } else if (value === 3) {
        getSupplierGoods(id, 0)
      }
    }
  }, [id, reportPeriodContext.lastSelectedReportId, change, value])


  useEffect(() => {
    if (hasPageMounted) {
      if (inputPageEmission === 0) {
        setChange(change + 1)
      } else {
        setInputPageEmission(0)
      }
    }

    setHasPageMounted(true)
  }, [value, reportPeriodContext.lastSelectedReportId])

  const getFileUploadSettings = (id: string) => {
    fileUploadSettingService.getAll(`?contactSupplierId=${id}`)
      .then(response => {
        setFileUploadSettings(response)
      })
      .catch(err => {
        console.log(err)
      })
  }

  const getSupplierGoods = (id: string, page: number) => {
    capitalGoodService.getAllWithPagination(id, page, goodsPerPage)
      .then(response => {
        setSupplierGoods(response.rows)
        setGoodsCount(response.count)
        setGoodsPage(page)
      })
      .catch(err => {
        console.log(err)
      })
  }

  const goodFactorChipLabel = (good: ICapitalGood): string => {
    if (good.kgCarbonEmission === null || good.kgCarbonEmission === undefined) {
      return 'No factor'
    }
    const factor = new Intl.NumberFormat('en-GB', { maximumSignificantDigits: 4 }).format(good.kgCarbonEmission)
    const unitName = good.measurementUnit?.name || 'unit'
    const perUnit = good.unitAmount && good.unitAmount !== 1 ? `per ${good.unitAmount} ${unitName}` : `/ ${unitName}`
    return `${factor} kgCO₂e ${perUnit}${good.isVerifiedBySupplier ? ' (verified)' : ''}`
  }

  const getSupplier = (id: string) => {
    contactSupplierService.getOneSupplier(id, true)
      .then(response => {
        setIsLoading(false)
        setSupplier(response)
        setEditSupplier({
          id: response.id,
          name: response.name,
          additionalInformation: response.additionalInformation,
          spendBasedConversionId: response.spendBasedConversionId,
          primaryContactName: response.primaryContactName,
          primaryContactEmail: response.primaryContactEmail,
          contactSupplierReference: response.contactSupplierReference,
          accreditations: [],
          otherAccreditationText: '',
          haveCarbonReductionTargets: response.haveCarbonReductionTargets,
          targetsVerified: response.targetsVerified,
          customFieldValues: response.customFieldValues,
          defaultSpendBasedConversionSourceId: response.defaultSpendBasedConversionSourceId,
        })
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }


  // Supplier Form
  const submitHandler = async (editSupplierForm: IPostContactSupplier) => {
    if (id && editSupplier) {
      contactSupplierService.updateSupplier(id, editSupplierForm)
        .then(response => {
          getSupplier(response.id!)
          setEditField('')
          refreshSuppliers()
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        })
        .catch(err => {
          if (err.response.data.message) {
            setErrorMessage(err.response.data.message)
            setErrorCode(err.response.data.code)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
        })
    }
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0 || newValue === 1) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  };

  const deleteHandler = (id: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this supplier as it has or currently is in use. Please review and update any associated emissions before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this ${screenLabelsContext.getLabel('supplier', { plural: false })}? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            contactSupplierService.delete(id)
              .then(() => {
                refreshSuppliers()
                userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                navigate('/organisation/suppliers')
              })
              .catch(err => {
                if (err.response.data.code === 1) {
                  userNotificationsContext.openDialog({
                    active: true, title: 'Action Not Allowed',
                    message: `You cannot delete this ${screenLabelsContext.getLabel('supplier', { plural: false })} as it has or currently is in use.`,
                    buttonCopy: 'Ok',
                    hideCancelButton: true
                  })
                } else {
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                }
                console.log(err)
              })
          }
        })
    }
  }


  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this ${screenLabelsContext.getLabel('supplier', { plural: false })}, you are recording the ${screenLabelsContext.getLabel('supplier', { plural: false })} and all associated goods as inactive. This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          contactSupplierService.makeSupplierInactive(id)
            .then(response => {
              getSupplier(id)
              setValue(0)
              refreshSuppliers()
              userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }

  function refreshSuppliers(): void {
    contactSupplierService.getAllSuppliers(true)
      .catch(err => {
        console.log(err)
      })
  }


  return (
    <>
      {supplier &&
        <PageOuterContainer>
          <PageHeading
            title={supplier.name}
            includeBackButton={true}
            backButtonLink='/organisation/suppliers'
            backButtonText={`Back to ${screenLabelsContext.getLabel('supplier', { plural: true })}`}
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.HideSourceOutlined}
            actionButtonHandler={() => deactivateHandler(supplier.id)}
            actionButtonText='Deactivate'
            hideShowActionButton={!supplier.active}
            includeSecondaryActionButton={true}
            secondaryActionButtonIcon={IconName.DeleteOutline}
            secondaryActionButtonHandler={() => deleteHandler(supplier.id, supplier.supplierInUse)}
            secondaryActionButtonText='Delete'
          />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details'  {...a11yProps(0)} />
              <Tab label='Import Settings'  {...a11yProps(1)} />
              <Tab label='Emissions' {...a11yProps(2)} />
              <Tab label={screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' })} {...a11yProps(3)} />
              <Tab label='Notes' {...a11yProps(4)} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <LoadingPage pageReady={!isLoading}>
              {id && supplier && editSupplier &&
                <SupplierForm
                  isEditPage={true}
                  submitHandler={submitHandler}
                  newSupplier={editSupplier}
                  supplier={supplier}
                  supplierActive={supplier.active}
                  errorMessage={errorMessage}
                  errorCode={errorCode}
                  editField={editField}
                  setEditField={setEditField}
                  setErrorMessage={setErrorMessage}
                  setErrorCode={setErrorCode}
                />}
            </LoadingPage>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {fileUploadSettings && fileUploadSettings.length === 0 &&
              <InformationBox
                text={`You do not have any import mappings for this ${screenLabelsContext.getLabel('supplier', { plural: false })}`}
                actionText={supplier.active ? "Click here to import data and map headings" : undefined}
                link={`/settings/data-upload/import-data?supplierId=${supplier.id}&dataType=emissions&start=upload`}
                icon={IconName.ErrorOutline}
              />
            }
            {fileUploadSettings && fileUploadSettings.length > 0 &&
              <>
                {fileUploadSettings.length === 0 ?
                  (
                    <InformationBox
                      text={`You do not have any import mappings for this ${screenLabelsContext.getLabel('supplier', { plural: false })}`}
                      actionText="Click here to import data and map headings"
                      link={`/settings/data-upload/import-data?supplierId=${supplier.id}&dataType=emissions&start=upload`}
                      icon={IconName.ErrorOutline}
                    />
                  ) : fileUploadSettings.map(fileUploadSetting => {
                    return (
                      <Box sx={{ mb: 2 }} key={fileUploadSetting.id}>
                        <Typography variant='subtitle1' sx={{ mb: 1 }}>{getTemplateFileName(fileUploadSetting.fileType)}</Typography>
                        <ImportMappingSupplierForm
                          fileUploadSetting={fileUploadSetting}
                          getFileUploadSettings={getFileUploadSettings}
                        />
                      </Box>
                    )
                  })
                }
              </>
            }
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            {supplier.active && userDetails?.roles.includes('admin') && !isReportPeriodLocked &&
              <Stack display='flex' flexDirection='row' justifyContent='flex-end' alignItems='center'>
                <Button variant='text' component={Link} to={`/settings/data-upload/import-data?supplierId=${supplier.id}&dataType=emissions&start=upload`} sx={{ mb: 1 }}>
                  <UploadOutlined sx={{ color: 'primary.main' }} />
                  <Typography sx={{ color: 'primary.main', display: 'inline' }}>Upload data</Typography>
                </Button>
              </Stack>}
            {!hasOpenReport
              && <Box>
                <ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
                {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
              </Box>
            }
            {hasOpenReport &&
              <LoadingPage pageReady={!isLoading}>
                {reportPeriodContext.lastSelectedReportId && id && (
                  <Emissions
                    includeDelete={false}
                    includeEdit={true}
                    queryString={`&contactSupplierId=${id}`}
                    backLink={`/organisation/suppliers/${id}?tabId=2`}
                    backText='Back to supplier'
                  />
                )}
              </LoadingPage>}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            {supplierGoods && supplierGoods.length === 0 &&
              <InformationBox
                icon={IconName.ErrorOutline}
                text={`This ${screenLabelsContext.getLabel('supplier', { plural: false })} is not linked to any ${screenLabelsContext.getLabel('product', { plural: true })}. You can link them from each ${screenLabelsContext.getLabel('product')}'s page`}
                actionText={`View ${screenLabelsContext.getLabel('product', { plural: true })}`}
                link='/organisation/products'
              />
            }
            {supplierGoods && supplierGoods.length > 0 &&
              <>
                <Grid container spacing={2}>
                  {supplierGoods.map((good) => (
                    <Grid key={good.id} xs={12} sm={12} md={6} item lg={4} xl={4}>
                      <Box sx={{ cursor: 'pointer', height: '100%' }} onClick={() => navigate(`/organisation/products/${good.id}`)}>
                        <EmissionsInputCard
                          title={good.name}
                          description={good.manufacturer?.name}
                          backgroundColor={'secondary.light'}
                          selected={false}
                          imageFilePath={good.capitalGoodImageFilePath}
                          icon={IconName.ShoppingBagOutlined}
                          showChip={true}
                          chipLabel={goodFactorChipLabel(good)}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  rowsPerPageOptions={[goodsPerPage]}
                  component="div"
                  count={goodsCount}
                  rowsPerPage={goodsPerPage}
                  page={goodsPage}
                  onPageChange={(_, newPage) => { if (id) getSupplierGoods(id, newPage) }}
                  onRowsPerPageChange={() => { }}
                  sx={{ mt: 2 }}
                />
              </>}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={4}>
            <NotesPanel
              relatedModelName='contactSupplier'
              relatedObjectId={supplier.id}
              canWrite={canWriteNotes}
              periodOptions={notePeriodOptions}
            />
          </CustomTabPanel>
        </PageOuterContainer>}
    </>
  )
}